import React, { useState } from 'react';
import { Trash2, Plus } from 'lucide-react';

const PostCallTab = ({ agent, setAgent }) => {
  const [newVariable, setNewVariable] = useState({ name: '', description: '' });

  const deliveryMethods = [
    { id: 'webhook', name: 'Webhook' },
    { id: 'email', name: 'Email' },
    { id: 'slack', name: 'Slack' },
    { id: 'google-sheets', name: 'Google Sheets' },
    { id: 'hubspot', name: 'HubSpot' },
    { id: 'salesforce', name: 'Salesforce' },
  ];

  const includeOptions = [
    { id: 'call_summary', name: 'Call Summary', description: 'A brief overview of the conversation including key points and outcomes' },
    { id: 'full_conversation', name: 'Full Conversation', description: 'Complete transcript of the entire conversation with timestamps' },
    { id: 'sentiment_analysis', name: 'Sentiment Analysis', description: 'Analysis of customer mood and emotional responses throughout the call' },
    { id: 'extracted_information', name: 'Extracted Information', description: 'Key data points extracted from the conversation' },
  ];

  const defaultVariables = [
    { id: '1', name: 'user_name', description: 'Name of the customer being called' },
    { id: '2', name: 'agent_name', description: 'Name of the virtual assistant' },
    { id: '3', name: 'address', description: "Customer's current address with zip code" },
    { id: '4', name: 'date_of_birth', description: "Customer's date of birth" },
    { id: '5', name: 'ssn_last_four', description: "Last four digits of the customer's social security number" },
  ];

  const extractedVariables = agent.postCallConfig?.extractedVariables || defaultVariables;

  const toggleInclude = (optionId) => {
    setAgent(prev => {
      const currentIncludes = prev.postCallConfig?.includes || [];
      const newIncludes = currentIncludes.includes(optionId)
        ? currentIncludes.filter(id => id !== optionId)
        : [...currentIncludes, optionId];
      return {
        ...prev,
        postCallConfig: {
          ...prev.postCallConfig,
          includes: newIncludes
        }
      };
    });
  };

  const addVariable = () => {
    if (newVariable.name && newVariable.description) {
      setAgent(prev => ({
        ...prev,
        postCallConfig: {
          ...prev.postCallConfig,
          extractedVariables: [
            ...(prev.postCallConfig?.extractedVariables || defaultVariables),
            { id: Date.now().toString(), ...newVariable }
          ]
        }
      }));
      setNewVariable({ name: '', description: '' });
    }
  };

  const removeVariable = (variableId) => {
    setAgent(prev => ({
      ...prev,
      postCallConfig: {
        ...prev.postCallConfig,
        extractedVariables: (prev.postCallConfig?.extractedVariables || defaultVariables).filter(v => v.id !== variableId)
      }
    }));
  };

  return (
    <div className="space-y-8">
      {/* Delivery Configuration */}
      <section className="bg-[#0d1117] border border-gray-800 rounded-xl p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-white">Delivery Method</h2>
          <button
            onClick={() => setAgent(prev => ({
              ...prev,
              postCallConfig: { ...prev.postCallConfig, deliveryMethod: '' }
            }))}
            className="flex items-center gap-2 text-red-400 hover:text-red-300 transition-colors"
          >
            <Trash2 className="w-4 h-4" />
            Remove
          </button>
        </div>
        
        <select
          value={agent.postCallConfig?.deliveryMethod || ''}
          onChange={(e) => setAgent(prev => ({
            ...prev,
            postCallConfig: { ...prev.postCallConfig, deliveryMethod: e.target.value }
          }))}
          className="w-48 bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2.5 text-white outline-none focus:border-teal-400"
        >
          <option value="">Select delivery method</option>
          {deliveryMethods.map(method => (
            <option key={method.id} value={method.id}>{method.name}</option>
          ))}
        </select>
      </section>

      {/* Include Options */}
      <section className="bg-[#0d1117] border border-gray-800 rounded-xl p-6">
        <h2 className="text-lg font-semibold text-white mb-6">Including</h2>
        
        <div className="grid grid-cols-2 gap-4">
          {includeOptions.map((option) => {
            const isSelected = agent.postCallConfig?.includes?.includes(option.id);
            return (
              <div
                key={option.id}
                onClick={() => toggleInclude(option.id)}
                className={`p-4 rounded-lg border cursor-pointer transition-all ${
                  isSelected 
                    ? 'bg-teal-500/10 border-teal-500' 
                    : 'bg-[#1a1a1f] border-gray-700 hover:border-gray-600'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center mt-0.5 ${
                    isSelected ? 'border-teal-400 bg-teal-400' : 'border-gray-500'
                  }`}>
                    {isSelected && (
                      <svg className="w-3 h-3 text-black" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    )}
                  </div>
                  <div>
                    <p className="text-white font-medium">{option.name}</p>
                    <p className="text-gray-500 text-sm mt-1">{option.description}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Extracted Variables */}
      <section className="bg-[#0d1117] border border-gray-800 rounded-xl p-6">
        <h2 className="text-lg font-semibold text-white mb-2">Extracted Variables</h2>
        <p className="text-gray-500 text-sm mb-6">
          Specify what variables you want to extract from the conversation. For each variable, provide a name and a description of how to extract it.
        </p>
        
        <div className="space-y-4">
          {extractedVariables.map((variable) => (
            <div 
              key={variable.id}
              className="flex items-center gap-4 bg-[#1a1a1f] rounded-lg overflow-hidden"
            >
              <input
                type="text"
                value={variable.name}
                readOnly
                className="flex-1 bg-transparent border-r border-gray-700 px-4 py-3 text-white outline-none"
              />
              <input
                type="text"
                value={variable.description}
                readOnly
                className="flex-[2] bg-transparent px-4 py-3 text-gray-400 outline-none"
              />
              <button
                onClick={() => removeVariable(variable.id)}
                className="p-3 text-red-400 hover:bg-red-400/10 transition-colors"
              >
                <Trash2 className="w-5 h-5" />
              </button>
            </div>
          ))}
          
          {/* Add New Variable */}
          <div className="flex items-center gap-4 bg-[#1a1a1f] rounded-lg overflow-hidden border border-dashed border-gray-700">
            <input
              type="text"
              value={newVariable.name}
              onChange={(e) => setNewVariable(prev => ({ ...prev, name: e.target.value }))}
              placeholder="Variable name"
              className="flex-1 bg-transparent border-r border-gray-700 px-4 py-3 text-white outline-none placeholder-gray-600"
            />
            <input
              type="text"
              value={newVariable.description}
              onChange={(e) => setNewVariable(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Description of how to extract"
              className="flex-[2] bg-transparent px-4 py-3 text-white outline-none placeholder-gray-600"
            />
            <button
              onClick={addVariable}
              disabled={!newVariable.name || !newVariable.description}
              className="p-3 text-teal-400 hover:bg-teal-400/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Plus className="w-5 h-5" />
            </button>
          </div>
        </div>
      </section>

      {/* Legacy Settings */}
      <section className="bg-[#0d1117] border border-gray-800 rounded-xl p-6">
        <h2 className="text-lg font-semibold text-white mb-6">Additional Post-Call Actions</h2>
        
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <span className="text-white">Generate Call Summary</span>
              <p className="text-gray-500 text-sm">AI-powered summary of each call</p>
            </div>
            <div 
              className={`w-12 h-7 rounded-full p-1 cursor-pointer transition-colors ${agent.postCallSummary ? 'bg-teal-400' : 'bg-gray-700'}`}
              onClick={() => setAgent(prev => ({ ...prev, postCallSummary: !prev.postCallSummary }))}
            >
              <div className={`w-5 h-5 rounded-full bg-white transition-transform ${agent.postCallSummary ? 'translate-x-5' : ''}`} />
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <span className="text-white">Save Call Transcript</span>
              <p className="text-gray-500 text-sm">Store full transcription of each call</p>
            </div>
            <div 
              className={`w-12 h-7 rounded-full p-1 cursor-pointer transition-colors ${agent.postCallTranscript ? 'bg-teal-400' : 'bg-gray-700'}`}
              onClick={() => setAgent(prev => ({ ...prev, postCallTranscript: !prev.postCallTranscript }))}
            >
              <div className={`w-5 h-5 rounded-full bg-white transition-transform ${agent.postCallTranscript ? 'translate-x-5' : ''}`} />
            </div>
          </div>

          <div>
            <label className="block text-gray-300 text-sm mb-2">Webhook URL</label>
            <input
              type="url"
              value={agent.postCallWebhook}
              onChange={(e) => setAgent(prev => ({ ...prev, postCallWebhook: e.target.value }))}
              className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2.5 text-white outline-none focus:border-teal-400"
              placeholder="https://your-webhook.com/endpoint"
            />
            <p className="text-gray-500 text-xs mt-2">Receive call data after each call ends</p>
          </div>

          <div>
            <label className="block text-gray-300 text-sm mb-2">Email Notification</label>
            <input
              type="email"
              value={agent.postCallEmail || ''}
              onChange={(e) => setAgent(prev => ({ ...prev, postCallEmail: e.target.value }))}
              className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2.5 text-white outline-none focus:border-teal-400"
              placeholder="notify@yourcompany.com"
            />
            <p className="text-gray-500 text-xs mt-2">Receive email notifications after calls (optional)</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PostCallTab;
